import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Star, Pencil } from "lucide-react";

// Department → Program architecture
// See /app/memory/brand_standards_v1.md §8
const DEPARTMENTS = [
  { key: "healing", label: "Healing" },
  { key: "education", label: "Education" },
  { key: "ecology", label: "Ecology" },
  { key: "culture", label: "Culture & Arts" },
  { key: "community", label: "Community" },
  { key: "development", label: "Development" },
  { key: "media", label: "Media" },
  { key: "funding", label: "Funding" },
  { key: "alliance", label: "Global Alliance" },
  { key: "research", label: "Research" },
  { key: "admin", label: "Administration" },
  { key: "archives", label: "Archives & Legacy" },
  { key: "observatory", label: "Observatory, Learning & Evaluation" },
];

const STATUSES = [
  { v: "planning", l: "Planning" },
  { v: "foundational_population", l: "Foundational · Population" },
  { v: "active", l: "Active" },
  { v: "archived", l: "Archived" },
];

const STATUS_STYLE = {
  planning: "bg-forest/10 text-forest",
  foundational_population: "bg-terracotta/15 text-terracotta",
  active: "bg-terracotta text-bone",
  archived: "bg-ink-soft/20 text-ink-soft",
};

const emptyForm = { department_key: "culture", name: "", mission: "", status: "planning", is_flagship: false, order: 100 };

export default function Programs() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const { data } = await api.get("/programs");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const byDept = {};
    for (const d of DEPARTMENTS) byDept[d.key] = [];
    for (const p of items) {
      if (!byDept[p.department_key]) byDept[p.department_key] = [];
      byDept[p.department_key].push(p);
    }
    return byDept;
  }, [items]);

  const totalActive = items.filter(p => p.status === "active").length;
  const totalFlagship = items.filter(p => p.is_flagship).length;
  const totalPopulating = items.filter(p => p.status === "foundational_population").length;

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      department_key: p.department_key, name: p.name, mission: p.mission || "",
      status: p.status || "planning", is_flagship: !!p.is_flagship, order: p.order ?? 100,
    });
    setDialogOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/programs/${editing.id}`, form);
        toast.success("Program updated");
      } else {
        await api.post("/programs", form);
        toast.success("Program added");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch {
      toast.error("Save failed");
    }
  };
  const del = async (p) => {
    if (!confirm(`Delete program "${p.name}"? This cannot be undone.`)) return;
    await api.delete(`/programs/${p.id}`);
    toast.success("Program deleted");
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Executive · Architecture</div>
          <h1 className="font-serif text-4xl mt-1">Programs</h1>
          <p className="text-ink-soft mt-2 max-w-2xl">Living initiatives inside each department. Programs are how a department becomes real — added, retired, or renamed without disturbing the institutional architecture.</p>
        </div>
        <button onClick={openNew} data-testid="new-program-btn" className="pill pill-primary text-sm">
          <Plus className="h-4 w-4" /> New Program
        </button>
      </div>

      {/* Signal strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Total programs" value={items.length} testId="stat-total" />
        <StatTile label="Active" value={totalActive} testId="stat-active" />
        <StatTile label="Foundational · populating" value={totalPopulating} testId="stat-populating" />
        <StatTile label="Flagship" value={totalFlagship} testId="stat-flagship" />
      </div>

      {/* Department groups */}
      <div className="space-y-6">
        {DEPARTMENTS.map((dept) => {
          const list = grouped[dept.key] || [];
          if (list.length === 0) return (
            <DeptEmpty key={dept.key} dept={dept} onAdd={() => { setForm({ ...emptyForm, department_key: dept.key }); setEditing(null); setDialogOpen(true); }} />
          );
          return (
            <section key={dept.key} className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid={`programs-dept-${dept.key}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-bone/40">
                <div className="flex items-center gap-3">
                  <h2 className="font-serif text-xl text-ink">{dept.label}</h2>
                  <span className="text-xs text-ink-soft">{list.length} program{list.length === 1 ? "" : "s"}</span>
                </div>
                <button onClick={() => { setForm({ ...emptyForm, department_key: dept.key }); setEditing(null); setDialogOpen(true); }}
                  className="text-xs uppercase tracking-widest text-ink-soft hover:text-terracotta transition-colors"
                  data-testid={`add-to-${dept.key}`}>+ Add program</button>
              </div>
              <table className="w-full text-sm">
                <thead className="text-ink-soft">
                  <tr className="text-left">
                    {["Program", "Status", "Flagship", "Order", ""].map(h => (
                      <th key={h} className="px-5 py-2 font-medium uppercase tracking-widest text-[10px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {list.map(p => (
                    <tr key={p.id} data-testid={`program-row-${p.id}`} className="hover:bg-bone/30">
                      <td className="px-5 py-3">
                        <div className="font-medium text-ink flex items-center gap-2">
                          {p.name}
                          {p.is_flagship && <Star className="h-3.5 w-3.5 text-terracotta fill-terracotta" />}
                        </div>
                        {p.mission && <div className="text-ink-soft text-xs mt-1 max-w-xl leading-relaxed">{p.mission}</div>}
                      </td>
                      <td className="px-5 py-3">
                        <Badge className={STATUS_STYLE[p.status] || "bg-forest/10 text-forest"}>
                          {STATUSES.find(s => s.v === p.status)?.l || p.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-ink-soft">{p.is_flagship ? "Yes" : "—"}</td>
                      <td className="px-5 py-3 text-ink-soft">{p.order}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="text-ink-soft hover:text-forest"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => del(p)} data-testid={`delete-${p.id}`} className="text-ink-soft hover:text-terracotta"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="bg-bone max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit program" : "New program"}</DialogTitle>
            <DialogDescription className="text-ink-soft">
              Living initiative inside a department. Program status: planning, foundational · population, active, or archived.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Department</Label>
                <Select value={form.department_key} onValueChange={(v) => setForm({ ...form, department_key: v })}>
                  <SelectTrigger data-testid="pf-department"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="pf-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="overline">Program name</Label>
              <Input required data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="overline">Mission</Label>
              <Textarea rows={4} data-testid="pf-mission" value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Order</Label>
                <Input type="number" data-testid="pf-order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "100", 10) })} />
              </div>
              <label className="flex items-center gap-2 mt-6 cursor-pointer" data-testid="pf-flagship-label">
                <input type="checkbox" checked={form.is_flagship} onChange={(e) => setForm({ ...form, is_flagship: e.target.checked })} data-testid="pf-flagship" />
                <span className="text-sm text-ink">Flagship program</span>
              </label>
            </div>
            <button type="submit" data-testid="pf-submit" className="pill pill-primary mt-2">
              {editing ? "Save changes" : "Add program"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, testId }) {
  return (
    <div className="bg-admin-surface border border-line rounded-xl p-4" data-testid={testId}>
      <div className="overline text-ink-soft">{label}</div>
      <div className="font-serif text-3xl text-ink mt-1">{value}</div>
    </div>
  );
}

function DeptEmpty({ dept, onAdd }) {
  return (
    <section className="border border-dashed border-line rounded-xl px-5 py-3 flex items-center justify-between bg-bone/20" data-testid={`programs-empty-${dept.key}`}>
      <div>
        <div className="font-serif text-lg text-ink">{dept.label}</div>
        <div className="text-xs text-ink-soft">No programs yet.</div>
      </div>
      <button onClick={onAdd} className="text-xs uppercase tracking-widest text-ink-soft hover:text-terracotta transition-colors">+ Add first program</button>
    </section>
  );
}
